import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const money = (cents, cur) => `${((cents || 0) / 100).toLocaleString(undefined, { style: 'currency', currency: (cur || 'aud').toUpperCase() })}`;
const MAX_POLLS = 8;

export function PaymentSuccess() {
  const { getPaymentStatus } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | pending | error
  const [info, setInfo] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    let timer;
    const poll = async () => {
      if (!sessionId) { setState('error'); return; }
      try {
        const data = await getPaymentStatus(sessionId);
        setInfo(data);
        if (data.payment_status === 'paid') { setState('paid'); return; }
        if (['failed', 'expired'].includes(data.payment_status)) { setState('error'); return; }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState('pending'); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState('error'); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, getPaymentStatus]);

  return (
    <div className="max-w-lg mx-auto px-4 py-16 fade-in">
      <div className="bg-white rounded-lg shadow-md p-8 border text-center" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        {state === 'polling' && (
          <>
            <Loader2 size={44} className="mx-auto mb-3 animate-spin" style={{ color: 'var(--navy)' }} />
            <h1 className="font-title text-2xl" style={{ color: 'var(--navy)' }}>Confirming your payment\u2026</h1>
          </>
        )}
        {state === 'paid' && (
          <>
            <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: '#2e7d32' }} />
            <h1 className="font-title text-2xl mb-1" style={{ color: 'var(--maroon)' }}>Thank you!</h1>
            <p className="text-[15px]">Your payment for <strong>{info?.item_name}</strong> of {money(info?.amount, info?.currency)} was successful.</p>
            <div className="flex gap-2 justify-center mt-6">
              <button onClick={() => navigate('/account')} className="px-4 py-2 rounded font-title text-sm" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>View my account</button>
              <button onClick={() => navigate('/')} className="px-4 py-2 rounded font-title text-sm" style={{ background: 'var(--sage)' }}>Home</button>
            </div>
          </>
        )}
        {state === 'pending' && (
          <>
            <Loader2 size={44} className="mx-auto mb-3" style={{ color: 'var(--olive)' }} />
            <h1 className="font-title text-2xl mb-1" style={{ color: 'var(--navy)' }}>Payment processing</h1>
            <p className="text-[15px]">This is taking a little longer than usual. Your account will update once confirmed.</p>
            <button onClick={() => navigate('/account')} className="mt-6 px-4 py-2 rounded font-title text-sm" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>Go to my account</button>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle size={48} className="mx-auto mb-3" style={{ color: 'var(--maroon)' }} />
            <h1 className="font-title text-2xl mb-1" style={{ color: 'var(--maroon)' }}>Something went wrong</h1>
            <p className="text-[15px]">We could not confirm this payment. If you were charged, please contact us.</p>
            <button onClick={() => navigate('/support')} className="mt-6 px-4 py-2 rounded font-title text-sm" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>Back to Support</button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="max-w-lg mx-auto px-4 py-16 fade-in">
      <div className="bg-white rounded-lg shadow-md p-8 border text-center" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <XCircle size={48} className="mx-auto mb-3" style={{ color: 'var(--olive)' }} />
        <h1 className="font-title text-2xl mb-1" style={{ color: 'var(--navy)' }}>Payment cancelled</h1>
        <p className="text-[15px]">No charge was made. You can return to the Support page any time.</p>
        <button onClick={() => navigate('/support')} className="mt-6 px-4 py-2 rounded font-title text-sm" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>Back to Support</button>
      </div>
    </div>
  );
}
